import { useCallback, useState, useRef } from 'react'
import { useFaturamentoApi } from '../../infra/faturamento'
import {
  IFaturamentoConfiguracao,
  INotaFiscalModelo,
  IFaturamentoConfiguracaoCompleta,
  IFaturamentoConfiguracaoSistema,
  IDescricaoPadraoNotaFiscal,
  ISubtitulosDisponiveis,
  IDataPadraoVencimento,
  IInserirFaturamentoConfiguracaoRequest,
  IAtualizarFaturamentoConfiguracaoRequest,
  IObterFaturamentoConfiguracaoPorClienteRequest,
  IObterFaturamentoCompletoPorClienteRequest,
  INotaFiscalConfig
} from '../../application/models'
import { useAppDispatch, useAppSelector } from '../../presentation/hooks/store-hooks'
import { setCreateClienteFormFaturamentoState } from '../../application/states/clientes'

export const useFaturamento = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Usar useRef para evitar recriação das funções da API
  const apiRef = useRef(useFaturamentoApi())
  const {
    obterFaturamentoConfiguracaoPorCliente,
    inserirFaturamentoConfiguracao,
    atualizarFaturamentoConfiguracao,
    obterModelosNotaFiscal,
    obterFaturamentoCompletoPorCliente,
    obterConfiguracoesSistema: obterConfiguracoesSistemaApi,
    obterDescricaoPadraoNotaFiscal,
    obterSubtitulosDisponiveis,
    obterDataPadraoVencimento: obterDataPadraoVencimentoApi
  } = apiRef.current

  const dispatch = useAppDispatch()
  const { createClienteForm } = useAppSelector((state) => state.clientes)

  // Obter configuração por cliente
  const obterConfiguracaoPorCliente = useCallback(async (data: IObterFaturamentoConfiguracaoPorClienteRequest): Promise<IFaturamentoConfiguracao | null> => {
    try {
      setLoading(true)
      setError(null)

      const result = await obterFaturamentoConfiguracaoPorCliente(data)
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao obter configuração de faturamento'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, []) 

  // Inserir configuração
  const inserirConfiguracao = useCallback(async (data: IInserirFaturamentoConfiguracaoRequest): Promise<IFaturamentoConfiguracao | null> => {
    try {
      setLoading(true)
      setError(null)

      const result = await inserirFaturamentoConfiguracao(data)
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao inserir configuração de faturamento'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, []) 

  // Atualizar configuração
  const atualizarConfiguracao = useCallback(async (data: IAtualizarFaturamentoConfiguracaoRequest): Promise<IFaturamentoConfiguracao | null> => {
    try {
      setLoading(true)
      setError(null)

      const result = await atualizarFaturamentoConfiguracao(data)
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar configuração de faturamento'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, []) 

  // Obter modelos de nota fiscal
  const obterModelos = useCallback(async (): Promise<INotaFiscalModelo[]> => {
    try {
      setLoading(true)
      setError(null)

      const result = await obterModelosNotaFiscal()
      return result || [] 
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao obter modelos de nota fiscal'
      setError(errorMessage)
      return [] 
    } finally {
      setLoading(false)
    }
  }, [])

  // Obter faturamento completo por cliente
  const obterFaturamentoCompleto = useCallback(async (data: IObterFaturamentoCompletoPorClienteRequest): Promise<IFaturamentoConfiguracaoCompleta[]> => {
    try {
      setLoading(true)
      setError(null)

      const result = await obterFaturamentoCompletoPorCliente(data)
      return result || []
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao obter faturamento completo'
      setError(errorMessage)
      return []
    } finally {
      setLoading(false)
    }
  }, []) 

  // Obter configurações do sistema
  const obterConfiguracoesSistema = useCallback(async (): Promise<IFaturamentoConfiguracaoSistema | null> => {
    try {
      setLoading(true)
      setError(null)

      const result = await obterConfiguracoesSistemaApi()
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao obter configurações do sistema'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, []) 

  // Obter descrição padrão
  const obterDescricaoPadrao = useCallback(async (): Promise<IDescricaoPadraoNotaFiscal | null> => {
    try {
      setLoading(true)
      setError(null)

      const result = await obterDescricaoPadraoNotaFiscal()
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao obter descrição padrão'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, []) 

  // Obter subtítulos disponíveis
  const obterSubtitulos = useCallback(async (): Promise<ISubtitulosDisponiveis | null> => {
    try {
      setLoading(true)
      setError(null)

      const result = await obterSubtitulosDisponiveis()
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao obter subtítulos disponíveis'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, []) 

  // Obter data padrão de vencimento
  const obterDataPadraoVencimento = useCallback(async (): Promise<IDataPadraoVencimento | null> => {
    try {
      setLoading(true)
      setError(null)

      const result = await obterDataPadraoVencimentoApi()
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao obter data padrão de vencimento'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, []) 

  //  SALVAR DADOS DO FATURAMENTO NO FORMULÁRIO
  const setCreateClienteFormFaturamento = useCallback((faturamento: any) => {
    dispatch(setCreateClienteFormFaturamentoState({ faturamento }))
  }, [dispatch])

  //  VALIDAR DADOS DA NOTA FISCAL
  const validateNotaFiscal = useCallback((dadosNotaFiscal: any): { isValid: boolean; errorMessage?: string } => {
    if (!dadosNotaFiscal) {
      return { isValid: false, errorMessage: "Preencha todos os campos obrigatórios da Nota Fiscal" }
    }

    // Se a nota fiscal está ativa, validar campos obrigatórios
    if (dadosNotaFiscal.Ativo === true) {
      if (!dadosNotaFiscal.IdNotaFiscalModeloDescricao) {
        return { isValid: false, errorMessage: "Modelo da nota fiscal é obrigatório quando ativa" }
      }

      if (!dadosNotaFiscal.DiaVencimento) {
        return { isValid: false, errorMessage: "Dia de vencimento é obrigatório quando a nota fiscal está ativa" }
      }
    }

    return { isValid: true }
  }, [])

  // OBTER DADOS ATUAIS DO FATURAMENTO
  const getCurrentFaturamentoData = useCallback((): any => {
    return createClienteForm.fields.faturamento || null
  }, [createClienteForm.fields.faturamento])

  // CONVERTER DADOS DA NOTA FISCAL PARA O FORMATO DO FATURAMENTO
  const convertNotaFiscalToFaturamento = useCallback((dadosNotaFiscal: any) => {
    return {
      IdFaturamentoConfiguracaoNotaFiscal: 0,
      IdCliente: 0,
      IdNotaFiscalModeloDescricao: dadosNotaFiscal.IdNotaFiscalModeloDescricao,
      NotaFiscalModeloDescricaoPersonalizado: dadosNotaFiscal.NotaFiscalModeloDescricaoPersonalizado,
      Ativo: dadosNotaFiscal.Ativo,
      DiaVencimento: dadosNotaFiscal.DiaVencimento,
      Inclusao: new Date().toISOString(),
      SimplesNacional: dadosNotaFiscal.SimplesNacional,
      Nome: dadosNotaFiscal.Nome
    }
  }, [])

    // SALVAR DADOS DA ABA NOTA FISCAL
  const saveNotaFiscalTabData = useCallback((notaFiscalRef: React.RefObject<any>): boolean => {
    if (notaFiscalRef.current) {
      const dadosNotaFiscalRaw = (notaFiscalRef.current as any).getRawNotaFiscalData?.();

      if (dadosNotaFiscalRaw) {
        const faturamentoConvertido = convertNotaFiscalToFaturamento(dadosNotaFiscalRaw);
        setCreateClienteFormFaturamento(faturamentoConvertido);
        return true;
      }
    }
    return false;
  }, [setCreateClienteFormFaturamento, convertNotaFiscalToFaturamento])

  // OBTER DADOS RAW DA NOTA FISCAL
  const getRawNotaFiscalData = useCallback((
    ativo: boolean,
    diaVencimento: number,
    simplesNacional: boolean,
    nome: string,
    descricaoPersonalizada: string,
    modelos: INotaFiscalModelo[]
  ): INotaFiscalConfig => {
    return {
      Ativo: ativo,
      DiaVencimento: diaVencimento,
      SimplesNacional: simplesNacional,
      Nome: nome,
      NotaFiscalModeloDescricaoPersonalizado: descricaoPersonalizada,
      IdNotaFiscalModeloDescricao: nome && modelos.length > 0 
        ? modelos.find(m => m.Nome === nome)?.IdNotaFiscalModeloDescricao || 0 
        : 0
    };
  }, [])

  return {
    // Estados
    loading,
    error,

    obterConfiguracaoPorCliente,
    inserirConfiguracao,
    atualizarConfiguracao,
    obterModelos,
    obterFaturamentoCompleto,
    obterConfiguracoesSistema,
    obterDescricaoPadrao,
    obterSubtitulos,
    obterDataPadraoVencimento,

    //NOVOS MÉTODOS PARA FORMULÁRIO
    setCreateClienteFormFaturamento,
    validateNotaFiscal,
    getCurrentFaturamentoData,
    convertNotaFiscalToFaturamento,
    saveNotaFiscalTabData,
    getRawNotaFiscalData,

    // Utilitários
    clearError: () => setError(null)
  }
}